import React from "react";
import {ProcessedResponse} from "../../state/state";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {createUseStyles, useTheme} from "react-jss";
import clsx from "clsx";
import {Theme} from "engage-client/theme";

const useStyles = createUseStyles<Theme>((theme) => ({
    responseSummary: {
        color: "white",
        display: "flex",
        flexDirection: "column",
        padding: 32,
        [theme.breakpoints.only("lg")] : {
            padding: 20,
        },
        [theme.breakpoints.only("xs")] : {
            padding: 20
        },
        background: theme.accessoryPanel.dashBoard.questionSummary.background,
        justifyContent: "space-between"
    },
    title: {
        margin: [18,0,28],
        color: theme.accessoryPanel.dashBoard.sectionTitleColor,
        [theme.breakpoints.only("lg")] : {
            margin: [0,0,12],
        },
        [theme.breakpoints.only("xs")] : {
            margin: [0,0,12],
        },
        fontWeight: theme.accessoryPanel.dashBoard.simpleDashboard ? 700 : 400
    },
    responses: {
        display: "flex",
        flexWrap: "wrap",
    },
    response: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: [14,6,0,6]
    },
    box: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: 44,
        height: 34,
        border: theme.accessoryPanel.dashBoard.simpleDashboard ? "" : "1px solid #FFFFFF",
        borderRadius: 5,
        marginBottom: 5,
        "&.unanswered": {
            opacity: 0.3,
        },
        fontWeight: theme.accessoryPanel.dashBoard.simpleDashboard ? 700 : 400
    },
    key: {
        display: "flex",
        justifyContent: "center",
        marginTop: 60,
        alignItems: "center",
        marginLeft: 6,
        color: theme.accessoryPanel.dashBoard.sectionTitleColor
    },
    keyBox: {
        border: theme.accessoryPanel.dashBoard.simpleDashboard ? "" : "1px solid #FFFFFF",
        borderRadius: 2,
        width: 12,
        height: 12,
        marginRight: 5,
        "&.leftSpace": {
            marginLeft: 15,
        }
    },
    correct: {
        color: theme.accessoryPanel.dashBoard.questionSummary.correct.answeredTextColor,
        backgroundColor: theme.accessoryPanel.dashBoard.questionSummary.correct.backgroundColor,
    },
    incorrect: {
        color: theme.accessoryPanel.dashBoard.questionSummary.correct.answeredTextColor,
        backgroundColor: theme.accessoryPanel.dashBoard.questionSummary.incorrect.backgroundColor,
    },
    poll: {
        backgroundColor: "#102949"
    },
    correctAnswersText: {
        color: theme.accessoryPanel.dashBoard.sectionTitleColor,
        marginLeft: 6
    },
    correctAnswersNumbers: {
        color: "white",
        fontWeight: 700,
        marginLeft: 2
    }
}));

export type QuestionSummaryProps = {
    responses: readonly (ProcessedResponse|undefined)[]
    totalQuestions: number
    questionsAsked: number
    correct?: number
    className?: string
}

export default function QuestionSummary({responses, totalQuestions, className, questionsAsked, correct}:QuestionSummaryProps) {

    const classes = useStyles();
    const theme:Theme = useTheme();

    const responseElements = [];
    let pollCount = 0;

    for(let i = 0; i < totalQuestions; i++) {
        if(i < questionsAsked) {
            const response = responses[i];
            if(response) {
                if (response.poll) {
                    pollCount++;
                }
                    responseElements.push(
                        <div className={classes.response} key={i}>
                            <DisplayText variant={TextVariant.s14med}
                                         className={clsx(classes.box, response.score > 0 ? classes.correct : response.poll ? classes.poll : classes.incorrect)}>
                                Q{i+1}
                            </DisplayText>
                            <DisplayText
                                variant={TextVariant.s14thin}>{Math.round(response.timeElapsed / 100) / 10}s</DisplayText>
                        </div>
                    );
            } else {
                responseElements.push(
                    <div className={classes.response} key={i}>
                        <DisplayText variant={TextVariant.s14med}
                                     className={clsx(classes.box, "unanswered")}>
                            Q{i+1}
                        </DisplayText>
                        <DisplayText variant={TextVariant.s14thin}>-</DisplayText>
                    </div>
                );
            }
        } else {
            responseElements.push(
                <div className={classes.response} key={i}>
                    <DisplayText variant={TextVariant.s14med}
                                 className={clsx(classes.box)}>
                        Q{i+1}
                    </DisplayText>
                </div>
            );
        }
    }

    return (
        <div className={clsx(classes.responseSummary, className)}>
            <DisplayText variant={TextVariant.s18reg} className={classes.title}>ANSWERS</DisplayText>
            <div>
                <DisplayText variant={TextVariant.s18med} className={classes.correctAnswersText}>Correct answers: <span className={classes.correctAnswersNumbers}>{correct}/{totalQuestions}</span></DisplayText>
                <div className={classes.responses}>
                    {responseElements}
                </div>
            </div>
            <div className={classes.key}>
                <div className={clsx(classes.keyBox, classes.correct)}/>
                <DisplayText variant={TextVariant.s14med} fontWeight={400}>Correct</DisplayText>
                <div className={clsx(classes.keyBox, classes.incorrect, "leftSpace")}/>
                <DisplayText variant={TextVariant.s14med} fontWeight={400}>Incorrect</DisplayText>
                {
                    pollCount > 0 &&  <>
                    <div className={clsx(classes.keyBox, classes.poll, "leftSpace")}/>
                    <DisplayText variant={TextVariant.s14med} fontWeight={400}>POLL</DisplayText></>
                }
               
            </div>
        </div>
    );
}